import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Divider,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'

const Privacy: React.FC = () => {
  const navigate = useNavigate()

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>

        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Last Updated: October 2025
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Introduction
          </Typography>
          <Typography paragraph>
            BlueBit Health LLC ("we," "our," or "us") operates the BlueBit TrialFinder mobile application and web platform 
            (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your 
            information when you use our Service.
          </Typography>
          <Typography paragraph>
            We are committed to protecting your privacy and ensuring the security of your personal health information in 
            compliance with applicable laws, including the Health Insurance Portability and Accountability Act (HIPAA) where applicable.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Information We Collect
          </Typography>
          
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            1. Account Information
          </Typography>
          <Typography paragraph>
            When you create an account, we collect:
          </Typography>
          <Typography component="ul">
            <li>Email address</li>
            <li>Name (first and last)</li>
            <li>Password (encrypted)</li>
            <li>Account preferences</li>
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            2. Health Information
          </Typography>
          <Typography paragraph>
            You may choose to enter health-related information including:
          </Typography>
          <Typography component="ul">
            <li>Medical conditions and diagnoses</li>
            <li>Medications and treatments</li>
            <li>Symptoms and medical history</li>
            <li>Test results and procedures</li>
            <li>BMI and vital statistics</li>
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold', color: 'warning.main' }}>
            IMPORTANT: We strongly encourage you to exclude or mask personal identifiers such as your full name, address, 
            Social Security Number, and medical record numbers when entering health information.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            3. Usage Information
          </Typography>
          <Typography paragraph>
            We automatically collect certain information about your use of the Service:
          </Typography>
          <Typography component="ul">
            <li>Clinical trial search history</li>
            <li>App usage patterns and features used</li>
            <li>Device information (iOS version, device model)</li>
            <li>Log data (timestamps, errors, crashes)</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            How We Use Your Information
          </Typography>
          <Typography paragraph>
            We use the collected information to:
          </Typography>
          <Typography component="ul">
            <li>Provide and maintain the Service</li>
            <li>Process your clinical trial searches</li>
            <li>Improve and personalize your experience</li>
            <li>Communicate with you about the Service</li>
            <li>Provide customer support</li>
            <li>Process subscription payments</li>
            <li>Comply with legal obligations</li>
            <li>Detect and prevent fraud or abuse</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Data Storage and Security
          </Typography>
          
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            On-Device Storage (iOS App)
          </Typography>
          <Typography paragraph>
            Your health data entered in the iOS mobile app is primarily stored on your device using secure local storage. 
            This data is not automatically synced to our cloud servers unless you explicitly use cloud features.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Cloud Storage (Web Platform)
          </Typography>
          <Typography paragraph>
            When you use the web platform or sync features, your data is stored on secure, encrypted servers. We implement 
            industry-standard security measures including:
          </Typography>
          <Typography component="ul">
            <li>Encryption in transit (TLS/SSL)</li>
            <li>Encryption at rest (AES-256)</li>
            <li>Access controls and authentication</li>
            <li>Regular security audits</li>
            <li>Secure backup procedures</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Third-Party Services
          </Typography>
          <Typography paragraph>
            We use third-party services that may collect information used to identify you:
          </Typography>
          <Typography component="ul">
            <li><strong>OpenAI API:</strong> We use OpenAI's API to process and analyze health information you provide. 
            Data sent to OpenAI is processed according to their privacy policy and data processing agreements.</li>
            <li><strong>ClinicalTrials.gov:</strong> We query the public ClinicalTrials.gov database to provide trial search results. 
            No personal information is sent to this service.</li>
            <li><strong>Apple App Store/StoreKit:</strong> For in-app purchase processing on iOS.</li>
            <li><strong>Payment Processors:</strong> For subscription management (no payment card data is stored on our servers).</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Data Sharing and Disclosure
          </Typography>
          <Typography paragraph>
            We do NOT sell your personal health information. We may share your information only in the following circumstances:
          </Typography>
          <Typography component="ul">
            <li><strong>With your consent:</strong> When you explicitly authorize us to share your information</li>
            <li><strong>Service providers:</strong> With trusted third parties who assist in operating our Service 
            (subject to strict confidentiality agreements)</li>
            <li><strong>Legal requirements:</strong> When required by law, court order, or legal process</li>
            <li><strong>Safety and protection:</strong> To protect rights, property, or safety of users or others</li>
            <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets 
            (with notice to affected users)</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Your Rights and Choices
          </Typography>
          <Typography paragraph>
            You have the following rights regarding your information:
          </Typography>
          <Typography component="ul">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and data</li>
            <li><strong>Export:</strong> Download your health data in portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Restriction:</strong> Limit how we use your information</li>
          </Typography>
          <Typography paragraph>
            To exercise these rights, contact us at info@bluebithealth.com
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Data Retention
          </Typography>
          <Typography paragraph>
            We retain your information for as long as your account is active or as needed to provide services. 
            When you delete your account, we will delete or anonymize your personal information within 30 days, 
            except where we are required to retain it for legal or regulatory purposes.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Children's Privacy
          </Typography>
          <Typography paragraph>
            Our Service is not intended for children under 13 years of age. We do not knowingly collect personal 
            information from children under 13. If you believe we have inadvertently collected such information, 
            please contact us immediately.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            International Users
          </Typography>
          <Typography paragraph>
            Our Service is operated in the United States. If you are accessing the Service from outside the United States, 
            please be aware that your information may be transferred to, stored, and processed in the United States where 
            our servers are located.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Changes to This Privacy Policy
          </Typography>
          <Typography paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
            the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this 
            Privacy Policy periodically.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Contact Us
          </Typography>
          <Typography paragraph>
            If you have questions about this Privacy Policy or our privacy practices, please contact us:
          </Typography>
          <Typography component="div" sx={{ ml: 2 }}>
            <strong>BlueBit Health LLC</strong><br />
            Email: info@bluebithealth.com<br />
            Website: www.bluebithealth.com
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 BlueBit Health LLC. All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

export default Privacy

